/* eslint-disable react/require-default-props */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-return-assign */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

import {
   Shell,
   SidebarArea,
   MainArea,
   Topbar,
   TopbarTitle,
   IconButton,
   Overlay,
} from '../styles/Shell';
import { useRecordScreenVisit } from '../hooks/useRecentScreens';

// ---------------------------------------------------------------------------
// Botão flutuante de colapsar
//
// Usa position: fixed para ficar ancorado na viewport — nunca rola junto
// com o conteúdo da página, independente do tamanho do Shell.
// left acompanha exatamente a largura da sidebar (72px ou 260px),
// deslocado -14px (metade do próprio botão) para ficar centrado na borda.
// Nunca é cortado pelo overflow do SidebarArea.
// ---------------------------------------------------------------------------
const CollapseToggle = styled.button`
   position: fixed;
   top: 36px;
   left: ${({ $collapsed }) =>
      $collapsed ? 'calc(72px - 14px)' : 'calc(260px - 14px)'};
   z-index: 200;

   width: 28px;
   height: 28px;
   border-radius: 50%;
   border: 1px solid ${({ theme }) => theme.colors.border};
   background: ${({ theme }) => theme.colors.surface};
   color: ${({ theme }) => theme.colors.textMuted};
   box-shadow: 0 1px 6px rgba(0, 0, 0, 0.1);

   display: flex;
   align-items: center;
   justify-content: center;
   cursor: pointer;

   transition:
      left 220ms cubic-bezier(0.2, 0, 0, 1),
      background 180ms ease,
      color 180ms ease,
      box-shadow 180ms ease;

   &:hover {
      background: rgba(0, 0, 0, 0.04);
      color: ${({ theme }) => theme.colors.textPrimary};
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.14);
   }

   @media (max-width: 900px) {
      display: none;
   }
`;

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------
export default function AppShell({ sidebar, title = 'MyShop', children }) {
   const location = useLocation();

   const [sidebarOpen, setSidebarOpen] = useState(false);
   const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

   // Histórico de "Últimas telas" da home. Fica aqui, e não na home, porque o
   // handoff (§4) pede captura no router — o histórico reflete a navegação
   // real, não só o que foi aberto a partir da home.
   useRecordScreenVisit();

   useEffect(() => {
      setSidebarOpen(false);
   }, [location.pathname]);

   useEffect(() => {
      if (sidebarOpen) document.body.style.overflow = 'hidden';
      else document.body.style.overflow = '';
      return () => (document.body.style.overflow = '');
   }, [sidebarOpen]);

   useEffect(() => {
      const onKeyDown = (e) => {
         if (e.key === 'Escape') setSidebarOpen(false);
      };
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
   }, []);

   const sidebarNode = React.isValidElement(sidebar)
      ? React.cloneElement(sidebar, {
           onNavigate: () => setSidebarOpen(false),
           collapsed: sidebarCollapsed,
        })
      : sidebar;

   // Marcações de IMPRESSÃO (2026-09-13, com o comprovante de movimento):
   // `data-app-shell` deixa o GlobalStyles colapsar o grid e o `min-height:
   // 100vh` no papel — sem isso o `height: 100vh` da sidebar e o mínimo do
   // Shell geram uma página em branco. `data-print-hide` tira do FLUXO o que
   // não vai para o papel; marcar aqui, e não por seletor de elemento, é
   // deliberado: `header` também é o cabeçalho de página (e o do próprio
   // documento impresso), então esconder `header` por elemento apagaria o
   // cabeçalho do comprovante junto.
   return (
      <Shell $collapsed={sidebarCollapsed} data-app-shell>
         <Overlay
            $open={sidebarOpen}
            onClick={() => setSidebarOpen(false)}
            data-print-hide
         />

         <SidebarArea
            $open={sidebarOpen}
            $collapsed={sidebarCollapsed}
            data-print-hide
         >
            {sidebarNode}
         </SidebarArea>

         {/* Botão fora do SidebarArea — não sofre com overflow */}
         <CollapseToggle
            type="button"
            $collapsed={sidebarCollapsed}
            onClick={() => setSidebarCollapsed((v) => !v)}
            title={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
            data-print-hide
         >
            {sidebarCollapsed ? (
               <FaChevronRight size={12} />
            ) : (
               <FaChevronLeft size={12} />
            )}
         </CollapseToggle>

         <MainArea>
            <Topbar data-print-hide>
               <IconButton
                  type="button"
                  onClick={() => setSidebarOpen((v) => !v)}
               >
                  ☰
               </IconButton>
               <TopbarTitle>{title}</TopbarTitle>
               <div style={{ width: 36 }} />
            </Topbar>

            {children}
         </MainArea>
      </Shell>
   );
}

AppShell.propTypes = {
   sidebar: PropTypes.node.isRequired,
   title: PropTypes.string,
   children: PropTypes.node.isRequired,
};
